// src/pages/Instrument_Runs/components/RunEditForm/RunEditForm.tsx
import { instrumentRunQueryKeys } from '@/config/queryKeys';
import { useApiClient } from '@/hooks/useApiClient';
import { useAssayRunConfigs, useAssays } from '@/hooks/useAssays';
import { extractFieldErrors } from '@/lib/api-errors';
import { applyServerFieldErrors, clearServerFieldErrors } from '@/lib/form-errors';
import { applyInstrumentRunsListRefresh } from '@/lib/instrumentRunsRefresh';
import { type Triple, mapFieldErrorsToForm } from '@/lib/map-field-errors';
import { deriveCustomerPlateLayout } from '@/pages/Instrument_Runs/components/SampleManager/helpers';
import { validateLibraryGroups } from '@/pages/Instrument_Runs/components/SampleManager/libraryGroupValidation';
import SampleManager from '@/pages/Instrument_Runs/components/SampleManager/SampleManager';
import { type SampleDraftInput, toSampleDraft } from '@/pages/Instrument_Runs/helpers';
import { useAppStore } from '@/stores/app';
import { SPECIES_LABELS, isControlSample } from '@/types';
import type {
  InstrumentRunDataRow,
  InstrumentRunDraftCreate,
  InstrumentRunDraftUpdate,
  InstrumentRunForm,
  InstrumentRunFormValues,
  InstrumentRunPublic,
  InstrumentRunSampleSize,
  Org,
  Species,
  StudyPublicGetMany,
} from '@/types';
import { Button } from '@nautilusbio/nb-design-system/Button';
import { Grid } from '@nautilusbio/nb-design-system/Grid';
import { Input } from '@nautilusbio/nb-design-system/Input';
import { Select } from '@nautilusbio/nb-design-system/Select';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import { Textarea } from '@nautilusbio/nb-design-system/Textarea';
import { toast } from '@nautilusbio/nb-design-system/Toaster';
import { useForm, useStore } from '@tanstack/react-form';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { CloudUploadIcon, SaveIcon, XIcon } from 'lucide-react';
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import z from 'zod';
import { useShallow } from 'zustand/react/shallow';

import irStyles from '../../styles.module.css';

import {
  activeLibraryGroupNames,
  findAssayRunConfig,
  isLibraryGroupCountForcedAll,
  sampleCountOptionsFor,
  validProteinLibraryCounts,
  validSampleCounts,
} from './assaySelection';
import {
  controlSampleInputs,
  countPopulatedLibraryPrepControls,
  deriveControlRows,
  resolveRunStudyName,
} from './controlRows';
import ControlsSection from './ControlsSection';
import { countDuplicateSampleNames, countDuplicateWellPositions, plateCoordinateKey } from './sampleDuplicates';
import styles from './styles.module.css';

const EDIT_READONLY_COLUMNS = ['flow_cell', 'lane', 'well_position', 'demux_index', 'org_name'];
// Compares only what a user can still change on a non-draft run: header fields are already
// read-only via InstrumentRunHeader's canEdit, and SampleManager's mount effects rewrite
// well_position/demux_index unprompted. Nested arrays — JSON.stringify is key-order sensitive.
const editableFingerprint = (v: InstrumentRunFormValues): string =>
  JSON.stringify([
    v.libraryGroupCount ?? '',
    ...(v.samples ?? []).map(s => [s.name ?? '', s.study_name ?? '', s.library_group ?? '', s.sample_group ?? '']),
  ]);
// Last resort for when no run config has loaded, or one loads with an empty valid_species.
// An empty assay list is a real problem to surface, not a gap to paper over.
const ALLOWED_SPECIES_FALLBACK: readonly Species[] = ['human', 'mouse'] as const;
const BUTTON_ICON_SIZE = 18;

export type RunEditFormHandle = {
  submit: () => void;
  saveDraft: () => void;
  submitDraft: () => void;
  form: InstrumentRunForm;
};

export type RunEditFormProps = {
  mode: 'create' | 'edit';
  run?: InstrumentRunPublic;
  orgId: string;
  organizations: Org[];
  studies: StudyPublicGetMany[];
  showHeader?: boolean;
  onSave: () => void;
  onCancel?: () => void;
};

const RunEditForm = forwardRef<RunEditFormHandle, RunEditFormProps>(function RunEditForm(
  { mode, run, orgId, organizations, showHeader, onSave, onCancel },
  ref,
) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const api = useApiClient();
  const queryClient = useQueryClient();

  const setShouldRefreshInstrumentRuns = useAppStore(useShallow(state => state.setShouldRefreshInstrumentRuns));

  const orgNameToId = useMemo(() => {
    const map = new Map<string, string>();
    for (const org of organizations) map.set(org.name, String(org.id));
    return map;
  }, [organizations]);

  const orgIdToName = useMemo(() => {
    const map = new Map<string, string>();
    for (const org of organizations) map.set(String(org.id), org.name);
    return map;
  }, [organizations]);

  const initialValues = useMemo((): InstrumentRunFormValues => {
    if (mode === 'edit' && run) {
      // Controls are stripped from the editable grid and re-derived from the assay run config at
      // submit (buildPayload), so an edit can never desync them from the assay's layout.
      const samples = (run.samples ?? [])
        .filter(s => !isControlSample(s.sample_type))
        .map(s => ({
          name: s.name ?? '',
          study_name: s.study_name ?? '',
          org_name: s.org_name ?? (s.org_id ? (orgIdToName.get(String(s.org_id)) ?? '') : ''),
          org_id: s.org_id ?? '',
          well_position: s.well_position ?? '',
          demux_index: s.demux_index ?? 0,
          // Form-local flag, derived on the way in — the API no longer sends is_control.
          is_control: isControlSample(s.sample_type),
          sample_type: s.sample_type ?? '',
          flow_cell: (s as InstrumentRunDataRow).flow_cell ?? null,
          lane: (s as InstrumentRunDataRow).lane ?? null,
          library_group: (s as InstrumentRunDataRow).library_group ?? null,
          sample_group: (s as InstrumentRunDataRow).sample_group ?? null,
        }));

      return {
        name: run.name ?? '',
        description: run.description ?? '',
        species: run.species ?? 'human',
        noOfSamples: String(run.num_samples ?? 12),
        assay_id: run.assay_id,
        samples,
        libraryGroupCount: countPopulatedLibraryPrepControls(run.samples),
      };
    }
    return {
      name: '',
      description: '',
      species: 'human',
      noOfSamples: '12',
      // Empty until the catalog loads; the first assay is selected in an effect below.
      assay_id: '',
      samples: [],
      // Resolved to the assay's smallest valid count once selectedAssayRunConfig loads (see the
      // libraryGroupCountOptions effect below) — not hardcoded, since an assay can have no
      // single-control option (e.g. [2, 3]).
      libraryGroupCount: undefined,
    };
  }, [mode, run, orgIdToName]);

  const schema = useMemo(
    () =>
      z.object({
        name: z
          .string()
          .min(1, t('create:run.validation.nameRequired'))
          .refine(v => !/['`]/.test(v), {
            message: t('create:run.validation.nameInvalidChars'),
          }),
        description: z.string().optional(),
        // Valid species now come from the selected assay's run config (dynamic per assay,
        // POR-322) rather than a fixed enum — non-empty string is the strongest static check.
        species: z.string().min(1),
        assay_id: z.string().min(1, t('create:run.validation.assayRequired')),
        noOfSamples: z.string().min(1),
        samples: z
          .array(
            z.object({
              name: z.string(),
              study_name: z.string(),
              org_name: z.string().optional(),
              well_position: z.string(),
              demux_index: z.number(),
              is_control: z.boolean().optional(),
              sample_type: z.string().optional(),
              flow_cell: z.string().nullable().optional(),
              lane: z.number().nullable().optional(),
              library_group: z.string().nullable().optional(),
              sample_group: z.string().nullable().optional(),
            }),
          )
          .superRefine((rows, ctx) => {
            const byName = new Map<string, number[]>();
            rows.forEach((r, i) => {
              const k = r.name?.trim();
              if (!k) return;
              const arr = byName.get(k) ?? [];
              arr.push(i);
              byName.set(k, arr);
            });
            for (const idxs of byName.values()) {
              if (idxs.length > 1) {
                for (const i of idxs) {
                  ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: [i, 'name'],
                    message: t('create:run.validation.duplicateInRun'),
                  });
                }
              }
            }

            // A repeated plate coordinate is a 422 at the draft call, so catch it here rather than
            // letting it surface as a bare toast. Generated rows are unique by construction; a CSV
            // upload or a manual well/demux edit is what collides.
            const byWell = new Map<string, number[]>();
            rows.forEach((r, i) => {
              const k = plateCoordinateKey(r);
              if (k === undefined) return;
              const arr = byWell.get(k) ?? [];
              arr.push(i);
              byWell.set(k, arr);
            });
            for (const idxs of byWell.values()) {
              if (idxs.length > 1) {
                for (const i of idxs) {
                  ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: [i, 'well_position'],
                    message: t('create:run.validation.duplicateWellInRun'),
                  });
                }
              }
            }

            // Pre-submit blocking for library_group, reusing validateLibraryGroups rather than
            // duplicating its rules — the ref, not the memo, since this schema is memoized on
            // [t] only and shouldn't be rebuilt on every count change.
            const { cellErrors: libraryGroupErrors } = validateLibraryGroups(
              rows as InstrumentRunDataRow[],
              activeLibraryGroupsRef.current,
              {
                required: t('create:run.validation.libraryGroupRequired'),
                tooLong: max => t('create:run.validation.libraryGroupTooLong', { max }),
                notActive: t('create:run.validation.libraryGroupNotActive'),
                unusedGroups: groups => t('create:run.validation.libraryGroupUnused', { groups: groups.join(', ') }),
              },
            );
            for (const [rowIdxStr, fieldErrors] of Object.entries(libraryGroupErrors)) {
              if (fieldErrors.library_group) {
                ctx.addIssue({
                  code: z.ZodIssueCode.custom,
                  path: [Number(rowIdxStr), 'library_group'],
                  message: fieldErrors.library_group,
                });
              }
            }
          }),
        libraryGroupCount: z.number().int().min(1).optional(),
      }),
    [t],
  );

  // Backend per-row errors and client-side duplicate-name issues stay in SEPARATE state so a
  // passing Zod run can't clear untouched rows' backend highlights. Merged below for SampleManager.
  const [sampleServerErrors, setSampleServerErrors] = useState<Triple[]>([]);
  const [sampleClientErrors, setSampleClientErrors] = useState<Triple[]>([]);
  const sampleErrors = useMemo(
    () => [...sampleServerErrors, ...sampleClientErrors],
    [sampleServerErrors, sampleClientErrors],
  );

  // When the user edits a row, drop that row's backend error so it can't be revived by the next
  // serverErrors re-push (the merged array recomputes whenever client-side dupe errors change).
  const handleSampleRowEdit = useCallback((rowIndex: number) => {
    setSampleServerErrors(prev => prev.filter(triple => triple.row !== rowIndex));
  }, []);

  const handleSampleRowsBulkEdit = useCallback(() => {
    setSampleServerErrors([]);
  }, []);

  const form = useForm({
    defaultValues: initialValues,
    validators: {
      onChange: ({ value }) => {
        const result = schema.safeParse(value);
        if (result.success) {
          setSampleClientErrors([]);
          return undefined;
        }

        const dupes: Triple[] = [];
        for (const issue of result.error.issues) {
          const [arrField, rowIdx, field] = issue.path;
          if (arrField !== 'samples' || typeof rowIdx !== 'number') continue;
          if (field === 'name' || field === 'well_position') {
            dupes.push({ row: rowIdx, field, msg: issue.message });
          }
        }
        setSampleClientErrors(dupes);

        return result.error.message;
      },
    },
    onSubmit: async ({ value }) => {
      await handleSubmit(value);
    },
  });

  const noOfSamplesStr = useStore(form.store, s => s.values.noOfSamples);
  const noOfSamples = Number(noOfSamplesStr ?? 12);
  const assayId = useStore(form.store, s => s.values.assay_id);
  const libraryGroupCountRaw = useStore(form.store, s => s.values.libraryGroupCount);
  // Subscribed rather than read once: the Controls table has to follow the study the user picks
  // for the customer rows, which is also the study buildPayload puts on every control.
  const runStudyName = useStore(form.store, s => resolveRunStudyName(s.values.samples ?? []));
  const flowcellSns = run?.consumables?.three_flow_cell_ids?.split(',').map(s => s.trim());
  const ownerOrgName = orgIdToName.get(String(orgId)) ?? '';

  // POR-196/POR-322: assay dropdown + species/sample-count/control options come from the real
  // Assay catalog. The run persists a real `assay_id` FK, so the selected Assay is a direct
  // id lookup.
  const { data: assays = [], isLoading: isAssaysLoading } = useAssays();
  const { data: assayRunConfigs = [], isLoading: isAssayRunConfigsLoading } = useAssayRunConfigs();
  const effectiveAssays = assays;
  const selectedAssay = useMemo(
    () => effectiveAssays.find(a => a.id === assayId) ?? effectiveAssays[0],
    [effectiveAssays, assayId],
  );
  const selectedAssayRunConfig = useMemo(
    () => findAssayRunConfig(assayRunConfigs, selectedAssay),
    [assayRunConfigs, selectedAssay],
  );
  // The dropdown falls back to displaying effectiveAssays[0] when assay_id is unset (e.g. before
  // the catalog loads on create), but that's display-only — sync the real field so the fallback
  // shown on screen is what actually gets submitted, instead of staying '' until the user touches
  // the Select themselves.
  useEffect(() => {
    if (!assayId && selectedAssay) {
      form.setFieldValue('assay_id', selectedAssay.id);
    }
  }, [assayId, selectedAssay, form]);
  // Clears libraryGroupCount on an actual assay switch so it re-resolves against the new assay's
  // options — but not on mount, or an edit-mode run's loaded count would be wiped immediately.
  const prevAssayIdRef = useRef(assayId);
  useEffect(() => {
    if (prevAssayIdRef.current !== assayId) {
      prevAssayIdRef.current = assayId;
      form.setFieldValue('libraryGroupCount', undefined);
    }
  }, [assayId, form]);
  // Customer rows must not land on a control coordinate — the assay's own control block decides
  // both the multiplexing depth and where customers start, so derive rather than hardcode.
  const customerPlateLayout = useMemo(
    () => deriveCustomerPlateLayout(selectedAssayRunConfig?.controls),
    [selectedAssayRunConfig],
  );
  // `??` alone doesn't catch a backend empty array — treat "loaded but empty" as "not loaded".
  // Memoized so the useMemo below doesn't see a new array identity, and recompute, every render.
  const speciesOptions = useMemo(
    () =>
      selectedAssayRunConfig?.valid_species && selectedAssayRunConfig.valid_species.length > 0
        ? selectedAssayRunConfig.valid_species
        : [...ALLOWED_SPECIES_FALLBACK],
    [selectedAssayRunConfig],
  );
  const sampleCountOptions = useMemo(() => sampleCountOptionsFor(selectedAssayRunConfig), [selectedAssayRunConfig]);
  const sampleCounts = useMemo(() => validSampleCounts(selectedAssayRunConfig), [selectedAssayRunConfig]);

  const libraryGroupCountOptions = useMemo(
    () => validProteinLibraryCounts(selectedAssayRunConfig),
    [selectedAssayRunConfig],
  );
  // The real working value: the form's own value when it's a valid option for this assay,
  // otherwise the assay's own smallest valid count (never a hardcoded 1 — see M5).
  const libraryGroupCount = libraryGroupCountOptions.includes(libraryGroupCountRaw ?? -1)
    ? (libraryGroupCountRaw as number)
    : libraryGroupCountOptions[0];
  // Writes the resolved default back into the form once options are known, so what's displayed
  // is also what actually gets submitted (not just a display-only fallback).
  useEffect(() => {
    if (libraryGroupCountRaw === undefined && libraryGroupCountOptions[0] !== undefined) {
      form.setFieldValue('libraryGroupCount', libraryGroupCountOptions[0]);
    }
  }, [libraryGroupCountRaw, libraryGroupCountOptions, form]);

  // Single source of truth for "which library groups are active," shared with
  // getControlRowsFromAssayRunConfig (controlRows.ts) so the Controls table and this value can
  // never disagree (fixes B2).
  const activeLibraryGroups = useMemo(
    () => activeLibraryGroupNames(selectedAssayRunConfig, noOfSamples, libraryGroupCount),
    [selectedAssayRunConfig, noOfSamples, libraryGroupCount],
  );
  // Once numSamples hits the assay's max, every library-prep control rides along regardless of
  // the count — showing the dropdown here would let a user "change" a count with no actual effect.
  const isLibraryGroupCountLocked = isLibraryGroupCountForcedAll(selectedAssayRunConfig, noOfSamples);
  // Zod's `schema` useMemo is keyed on [t] only and shouldn't be rebuilt on every count change —
  // the .superRefine cross-check below reads this ref at validation time instead.
  const activeLibraryGroupsRef = useRef<string[]>(activeLibraryGroups);
  useEffect(() => {
    activeLibraryGroupsRef.current = activeLibraryGroups;
  }, [activeLibraryGroups]);

  const toSampleDraftBound = useCallback((s: SampleDraftInput) => toSampleDraft(s, orgNameToId), [orgNameToId]);

  // `num_samples` counts CUSTOMER samples only, but submit requires every physical well — so the
  // assay's controls ride along in `samples` without being counted. Unpopulated (declined) control
  // rows are excluded: they exist only to keep the Controls table's shape.
  const buildPayload = useCallback(
    (value: InstrumentRunFormValues) => {
      const identity = { orgName: ownerOrgName, studyName: resolveRunStudyName(value.samples) };
      const controlDrafts = controlSampleInputs(
        deriveControlRows(
          selectedAssayRunConfig,
          selectedAssay?.assay_type,
          Number(value.noOfSamples),
          libraryGroupCount,
          identity,
        ),
        identity,
        orgId,
      ).map(toSampleDraftBound);

      return {
        assay_id: value.assay_id,
        name: value.name,
        description: value.description ?? null,
        species: value.species as Species,
        num_samples: Number(value.noOfSamples) as InstrumentRunSampleSize,
        samples: [...value.samples.map(toSampleDraftBound), ...controlDrafts],
      };
    },
    [orgId, ownerOrgName, selectedAssay, selectedAssayRunConfig, libraryGroupCount, toSampleDraftBound],
  );

  const handleApiError = useCallback(
    (error: unknown, value: InstrumentRunFormValues) => {
      const fieldErrors = extractFieldErrors(error);
      const { formErrors, arrayErrors, unmapped } = mapFieldErrorsToForm(fieldErrors, {
        arrayFields: ['samples'],
        byNameLookup: { fieldName: 'name', rows: value.samples },
      });

      // Top-level field errors surface inline on the form. Mark the error as handled so callers
      // don't also fire the generic failure toast (the inline message is the user-facing signal).
      let handled = false;
      if (Object.keys(formErrors).length > 0) {
        applyServerFieldErrors(form, formErrors);
        handled = true;
      }

      const sampleErrs = arrayErrors.samples;
      if (sampleErrs?.length) {
        setSampleServerErrors(sampleErrs);
        // The kind:'row' backend envelope is a generic per-sample validation error, so only use
        // the name-conflict copy when the messages are actually conflicts; otherwise a neutral
        // "samples have errors" message. The precise reason is in each row's tooltip either way.
        const allNameConflicts = sampleErrs.every(e => /already exist/i.test(e.msg));
        toast.error(
          allNameConflicts
            ? t('create:run.error.sampleConflictsBackend', { count: sampleErrs.length })
            : t('create:run.error.sampleErrorsBackend', { count: sampleErrs.length }),
        );
        return true;
      }

      if (unmapped.length > 0) {
        toast.error(unmapped[0]);
        return true;
      }

      return handled;
    },
    [form, t],
  );

  const handleSaveDraft = useCallback(
    async (value: InstrumentRunFormValues) => {
      if (!value.name) {
        toast.error(t('create:run.validation.nameRequired'));
        return;
      }
      setSampleServerErrors([]);
      clearServerFieldErrors(form);
      try {
        const payload: InstrumentRunDraftCreate = { ...buildPayload(value) };
        const created = await api.post<InstrumentRunPublic>(instrumentRunQueryKeys.endpoints.draft(orgId), payload);
        if (!created) return;
        await applyInstrumentRunsListRefresh(orgId, queryClient, setShouldRefreshInstrumentRuns);
        toast.success(`${value.name} ${t('create:run.created')}`);
        void navigate({ to: '/instrument_runs/$instrumentRunId', params: { instrumentRunId: created.id } });
      } catch (error) {
        if (import.meta.env.DEV) console.error(error);
        setSampleServerErrors([]);
        const handled = handleApiError(error, value);
        if (!handled) toast.error(t('create:run.failedToSaveDraft'));
      }
    },
    [t, api, orgId, queryClient, setShouldRefreshInstrumentRuns, buildPayload, navigate, form, handleApiError],
  );

  const handleSubmit = useCallback(
    async (value: InstrumentRunFormValues) => {
      if (!value.name) {
        toast.error(t('create:run.validation.nameRequired'));
        return;
      }
      setSampleServerErrors([]);
      clearServerFieldErrors(form);
      try {
        const draftPayload: InstrumentRunDraftCreate = {
          ...buildPayload(value),
        };
        const draft = await api.post<InstrumentRunPublic>(instrumentRunQueryKeys.endpoints.draft(orgId), draftPayload);
        if (!draft) return;
        await api.post(instrumentRunQueryKeys.endpoints.submit(orgId, draft.id), {});
        await applyInstrumentRunsListRefresh(orgId, queryClient, setShouldRefreshInstrumentRuns);
        toast.success(`${value.name} ${t('create:run.created')}`);
        void navigate({
          to: '/instrument_runs/$instrumentRunId',
          params: { instrumentRunId: draft.id },
        });
        onSave();
      } catch (error) {
        if (import.meta.env.DEV) console.error(error);
        setSampleServerErrors([]);
        const handled = handleApiError(error, value);
        if (!handled) toast.error(t('create:run.failed'));
      }
    },
    [t, api, orgId, queryClient, setShouldRefreshInstrumentRuns, navigate, onSave, buildPayload, form, handleApiError],
  );

  const handleSaveEdit = useCallback(
    async (value: InstrumentRunFormValues) => {
      if (!run) return;
      // Draft-only PUT (403s otherwise); consumables PATCH saves independently — POR-415. Warn
      // rather than drop silently if the user changed something this save can't persist.
      if (run.status !== 'draft') {
        if (editableFingerprint(value) !== editableFingerprint(initialValues))
          toast.warning(t('create:run.editsNotSavedNonDraft'));
        return;
      }
      if (!value.name) {
        toast.error(t('create:run.validation.nameRequired'));
        return;
      }
      setSampleServerErrors([]);
      clearServerFieldErrors(form);
      try {
        const payload: InstrumentRunDraftUpdate = { ...buildPayload(value) };
        const updated = await api.put<InstrumentRunPublic>(
          instrumentRunQueryKeys.endpoints.detail(orgId, run.id),
          payload,
        );
        if (!updated) return;
        await queryClient.invalidateQueries({
          queryKey: instrumentRunQueryKeys.detail(orgId, run.id),
        });
        await applyInstrumentRunsListRefresh(orgId, queryClient, setShouldRefreshInstrumentRuns);
        toast.success(`${value.name} ${t('create:run.updated')}`);
        onSave();
      } catch (error) {
        if (import.meta.env.DEV) console.error(error);
        setSampleServerErrors([]);
        const handled = handleApiError(error, value);
        if (!handled) toast.error(t('create:run.failed'));
      }
    },
    [
      run,
      t,
      api,
      orgId,
      queryClient,
      setShouldRefreshInstrumentRuns,
      onSave,
      buildPayload,
      form,
      handleApiError,
      initialValues,
    ],
  );

  const handleSubmitDraft = useCallback(
    async (value: InstrumentRunFormValues) => {
      if (!run) return;
      if (!value.name) {
        toast.error(t('create:run.validation.nameRequired'));
        return;
      }
      // Submitting a draft finalizes it, so block in-run duplicates here just like the
      // create-mode submit does (the duplicate cells are already highlighted by the onChange
      // Zod validator). handleSaveEdit, the plain save, stays unblocked on purpose.
      const dupes = countDuplicateSampleNames(value.samples);
      if (dupes > 0) {
        toast.error(t('create:run.error.sampleConflictsInRun', { count: dupes }));
        return;
      }
      const wellDupes = countDuplicateWellPositions(value.samples);
      if (wellDupes > 0) {
        toast.error(t('create:run.error.sampleWellConflictsInRun', { count: wellDupes }));
        return;
      }
      setSampleServerErrors([]);
      clearServerFieldErrors(form);
      try {
        const payload: InstrumentRunDraftUpdate = { ...buildPayload(value) };
        const updated = await api.put<InstrumentRunPublic>(
          instrumentRunQueryKeys.endpoints.detail(orgId, run.id),
          payload,
        );
        if (!updated) return;
        await api.post(instrumentRunQueryKeys.endpoints.submit(orgId, run.id), {});
        await queryClient.invalidateQueries({
          queryKey: instrumentRunQueryKeys.detail(orgId, run.id),
        });
        await applyInstrumentRunsListRefresh(orgId, queryClient, setShouldRefreshInstrumentRuns);
        toast.success(`${value.name} ${t('create:run.submitted')}`);
        void navigate({ to: '/instrument_runs/$instrumentRunId', params: { instrumentRunId: run.id } });
      } catch (error) {
        if (import.meta.env.DEV) console.error(error);
        setSampleServerErrors([]);
        const handled = handleApiError(error, value);
        if (!handled) toast.error(t('create:run.failed'));
      }
    },
    [run, t, api, orgId, queryClient, setShouldRefreshInstrumentRuns, buildPayload, navigate, form, handleApiError],
  );

  // Create-mode submit: surface in-table duplicates with an explicit toast (distinct from the
  // backend "already exists" conflict toast). form.handleSubmit() then re-runs validation, which
  // keeps the duplicate cells highlighted and blocks the API call.
  const submitCreate = useCallback(() => {
    const dupes = countDuplicateSampleNames(form.state.values.samples);
    if (dupes > 0) {
      toast.error(t('create:run.error.sampleConflictsInRun', { count: dupes }));
    }
    const wellDupes = countDuplicateWellPositions(form.state.values.samples);
    if (wellDupes > 0) {
      toast.error(t('create:run.error.sampleWellConflictsInRun', { count: wellDupes }));
    }
    void form.handleSubmit();
  }, [form, t]);

  useImperativeHandle(
    ref,
    () => ({
      submit: () => {
        if (mode === 'edit') void handleSaveEdit(form.state.values);
        else submitCreate();
      },
      saveDraft: () => {
        if (mode === 'edit') void handleSaveEdit(form.state.values);
        else void handleSaveDraft(form.state.values);
      },
      submitDraft: () => void handleSubmitDraft(form.state.values),
      form: form as unknown as InstrumentRunForm,
    }),
    [mode, form, handleSaveEdit, handleSaveDraft, handleSubmitDraft, submitCreate],
  );

  // A current value the catalog no longer offers (e.g. an old noOfSamples='4' run) would render
  // the Select blank while the form still submits the stale value. Surface it as a labeled
  // "legacy" option instead, so the Select always shows something truthful.
  const withLegacyOption = useCallback(
    (options: string[], currentValue: string | undefined): { values: string[]; labels: string[] } => {
      if (currentValue !== undefined && currentValue !== '' && !options.includes(currentValue)) {
        return { values: [...options, currentValue], labels: [...options, `${currentValue} (legacy)`] };
      }
      return { values: options, labels: options };
    },
    [],
  );
  const speciesStr = useStore(form.store, s => s.values.species);
  const sampleCountOptionsWithLegacy = useMemo(
    () => withLegacyOption(sampleCountOptions, noOfSamplesStr),
    [sampleCountOptions, noOfSamplesStr, withLegacyOption],
  );
  const speciesOptionsWithLegacy = useMemo(() => {
    const { values, labels } = withLegacyOption(speciesOptions, speciesStr);

    // Species values are lower_snake on the wire; map to display labels so the dropdown does
    // not read "human" / "mouse". A legacy value not in SPECIES_LABELS keeps its "(legacy)"
    // label from withLegacyOption.
    return {
      values,
      labels: labels.map((label, i) => {
        const value = values[i];
        return value !== undefined && value in SPECIES_LABELS ? SPECIES_LABELS[value as Species] : label;
      }),
    };
  }, [speciesOptions, speciesStr, withLegacyOption]);

  return (
    <Stack className={styles.container} fullWidth gap={4} orientation="vertical">
      {showHeader && (
        <Stack className={irStyles.headerCard} fullWidth orientation="vertical">
          {/* Name + Assay Version */}
          <Grid
            alignItems="center"
            className={irStyles.headerTitleRow}
            fullWidth
            gap={6}
            gridTemplateColumns="1fr .2fr"
          >
            <form.Field name="name">
              {field => (
                <Input
                  data-testid="run-name-input"
                  fullWidth
                  id="run-name"
                  label={{
                    children: t('header.name', { ns: 'runs' }),
                    htmlFor: 'run-name',
                    id: 'label-run-name',
                  }}
                  onChange={e => field.handleChange((e.target as HTMLInputElement).value)}
                  value={(field.state.value as string) ?? ''}
                />
              )}
            </form.Field>
            <form.Field name="assay_id">
              {field => (
                <Select
                  allowClear={false}
                  classNames={{
                    button: irStyles.headerSelect,
                    container: irStyles.assaySelect,
                  }}
                  data-testid="select-assay-id"
                  id="assay-id"
                  isPending={isAssaysLoading}
                  label={{
                    children: t('header.assay', { ns: 'runs' }),
                    id: 'label-assay-id',
                  }}
                  onChange={idx => {
                    const selected = effectiveAssays[idx];
                    if (selected !== undefined) field.handleChange(selected.id);
                  }}
                  options={effectiveAssays.map(a => a.assay_friendly_name)}
                  value={effectiveAssays.findIndex(a => a.id === selectedAssay?.id)}
                />
              )}
            </form.Field>
          </Grid>

          {/* Description */}
          <form.Field name="description">
            {field => (
              <Textarea
                data-testid="run-description-textarea"
                label={{
                  children: t('header.description', { ns: 'runs' }),
                  htmlFor: 'run-description',
                  id: 'label-run-description',
                }}
                onChange={e => field.handleChange((e.target as HTMLTextAreaElement).value)}
                value={(field.state.value as string) ?? ''}
              />
            )}
          </form.Field>

          {/* Samples + Species — 2 items each at 1/3 width, start-aligned */}
          <div className={irStyles.headerSelectsRow}>
            <div className={irStyles.headerSelectItem}>
              <form.Field name="noOfSamples">
                {field => (
                  <Select
                    allowClear={false}
                    classNames={{ button: irStyles.headerSelect }}
                    data-testid="select-no-of-samples"
                    id="no-of-samples"
                    isPending={isAssayRunConfigsLoading}
                    label={{
                      children: t('header.samples', { ns: 'runs' }),
                      id: 'label-no-of-samples',
                    }}
                    onChange={idx => {
                      const val = sampleCountOptionsWithLegacy.values[idx];
                      if (val !== undefined) field.handleChange(val);
                    }}
                    options={sampleCountOptionsWithLegacy.labels}
                    value={sampleCountOptionsWithLegacy.values.indexOf(
                      (field.state.value as string) ?? sampleCountOptionsWithLegacy.values[0],
                    )}
                  />
                )}
              </form.Field>
            </div>
            <div className={irStyles.headerSelectItem}>
              <form.Field name="species">
                {field => (
                  <Select
                    allowClear={false}
                    classNames={{ button: irStyles.headerSelect }}
                    data-testid="select-species"
                    id="species"
                    isPending={isAssayRunConfigsLoading}
                    label={{
                      children: t('header.species', { ns: 'runs' }),
                      id: 'label-species',
                    }}
                    onChange={idx => {
                      const val = speciesOptionsWithLegacy.values[idx];
                      if (val !== undefined) field.handleChange(val as Species);
                    }}
                    options={speciesOptionsWithLegacy.labels}
                    value={speciesOptionsWithLegacy.values.indexOf(field.state.value as string)}
                  />
                )}
              </form.Field>
            </div>
          </div>
        </Stack>
      )}

      {/* Controls — display only, computed from the selected assay's run config. Falls back to
          the hardcoded Tau layout (getControlRows) when the run config hasn't loaded/404s. */}
      <ControlsSection
        assayRunConfig={selectedAssayRunConfig}
        assayType={selectedAssay?.assay_type}
        flowcellSns={flowcellSns}
        libraryGroupCount={libraryGroupCount}
        numSamples={noOfSamples}
        ownerOrgName={ownerOrgName}
        runStudyName={runStudyName}
      />

      {isAssayRunConfigsLoading ? (
        <form.Field name="libraryGroupCount">
          {() => (
            <Select
              allowClear={false}
              data-testid="select-library-group-count"
              id="library-group-count"
              isPending
              label={{
                children: t('controls.libraryGroupCount', { ns: 'runs' }),
                id: 'label-library-group-count',
              }}
              onChange={() => {}}
              options={[]}
              value={-1}
            />
          )}
        </form.Field>
      ) : (
        libraryGroupCountOptions.length > 1 &&
        !isLibraryGroupCountLocked && (
          <form.Field name="libraryGroupCount">
            {field => (
              <Select
                allowClear={false}
                data-testid="select-library-group-count"
                id="library-group-count"
                label={{
                  children: t('controls.libraryGroupCount', { ns: 'runs' }),
                  id: 'label-library-group-count',
                }}
                onChange={idx => {
                  const val = libraryGroupCountOptions[idx];
                  if (val !== undefined) field.handleChange(val);
                }}
                options={libraryGroupCountOptions.map(String)}
                value={libraryGroupCountOptions.indexOf(libraryGroupCount)}
              />
            )}
          </form.Field>
        )
      )}

      {/* Samples — non-control rows only */}
      <SampleManager
        activeLibraryGroups={activeLibraryGroups}
        customerLayout={customerPlateLayout}
        form={form as unknown as InstrumentRunForm}
        noOfSamples={noOfSamples}
        onSampleRowEdit={handleSampleRowEdit}
        onSampleRowsBulkEdit={handleSampleRowsBulkEdit}
        readOnlyColumns={mode === 'edit' ? EDIT_READONLY_COLUMNS : []}
        serverErrors={sampleErrors}
        showCopyToRows={mode === 'edit'}
        validSampleCounts={sampleCounts}
      />

      {/* Footer — only shown when buttons are not hosted in a parent action row */}
      {!showHeader && (
        <Stack className={styles.footer} fullWidth gap={3} justifyContent="end" orientation="horizontal">
          {onCancel && (
            <Button data-testid="run-edit-cancel" onPress={onCancel} visual="outline">
              <XIcon size={BUTTON_ICON_SIZE} />
              {t('cancel', { ns: 'general' })}
            </Button>
          )}

          {mode === 'create' && (
            <Button
              data-testid="run-save-draft"
              onPress={() => void handleSaveDraft(form.state.values)}
              visual="outline"
            >
              <SaveIcon size={BUTTON_ICON_SIZE} />
              {t('create:run.saveAsDraft')}
            </Button>
          )}

          {mode === 'edit' && run?.status === 'draft' && (
            <Button
              data-testid="run-save-draft"
              onPress={() => void handleSaveEdit(form.state.values)}
              visual="outline"
            >
              <SaveIcon size={BUTTON_ICON_SIZE} />
              {t('create:run.saveAsDraft')}
            </Button>
          )}

          {/* Hidden on a non-draft edit: unlike the page's own Save it never submits consumables,
              so on a submitted run it can only discard the one edit that status still allows. */}
          {(mode === 'create' || run?.status === 'draft') && (
            <Button
              data-testid={mode === 'edit' ? 'run-save' : 'run-submit'}
              onPress={() => {
                if (mode === 'edit') {
                  void handleSaveEdit(form.state.values);
                } else {
                  submitCreate();
                }
              }}
              visual="primary"
            >
              <CloudUploadIcon size={BUTTON_ICON_SIZE} />
              {mode === 'edit' ? t('save', { ns: 'general' }) : t('create:run.submit', { defaultValue: 'Submit' })}
            </Button>
          )}
        </Stack>
      )}
    </Stack>
  );
});

RunEditForm.displayName = 'RunEditForm';

export default RunEditForm;
